import { auth, db } from 'boot/firebase';
import IdxDb from 'boot/indexedDb';
import { network } from 'boot/network';
import { liveQuery } from 'dexie';
import { collection, doc, FieldPath, FieldValue, Timestamp } from 'firebase/firestore';
import {
  cloneDeep, every, forEach, get, groupBy, isArray, isEmpty, isEqual, isEqualWith,
  omit, orderBy, reject, some, split, unionWith,
} from 'lodash';
import { uid } from 'quasar';
import { AUTH_SESSIONS_INDEX_DB } from 'src/constants/authorization';
import { ENCRYPTED_FIELDS } from 'src/constants/encryption';
import AppUtils from 'src/utils/AppUtils';
import { addBackupDecryptionKey, BACKUP_DATA, BACKUP_NOTES, clearBackups } from 'src/utils/backupUtils';
import { createEncryptedDataObject, readSecret } from 'src/utils/CryptoUtils';
import {
  decrypt, firebaseTimestampObjectToTimestamp, getFieldValueData,
  getFieldValueWithUpdatedData, isTimestampObject, serializeIdxDbData,
} from 'src/utils/FirestoreUtils';
import { logEvent } from 'src/utils/LogEvent';
import { isQuotaExceededError, logStorageQuotaEvent, notifyStorageQuotaExceeded } from 'src/utils/StorageQuotaUtils';

/** @typedef {import('firebase/firestore').WhereFilterOp} WhereFilterOp */
/** @typedef {import('dexie').WhereClause} WhereClause */
/** @typedef {import('dexie').Table} Table */
/** @typedef {import('dexie').Collection} Collection */

/** @typedef {Array<string, WhereFilterOp & string, any>} Constraint */
/** @typedef {{
 *  query: () => Promise<Object | Object[]>,
 *  path: string,
 *  type: 'document' | 'query',
 *  id?: string,
 *  orderBy?: string | null,
 *  orderDirection?: 'asc' | 'desc' | null
 * }} IdxDbRef */
/** @typedef {{
 * id: string,
 * operation: 'create' | 'update' | 'delete',
 * collectionName: string,
 * documentId: string,
 * payload?: Object,
 * timestamp: Date
 * }} DbChangeDocument */

/**
 * Added to every document's id stored in our IndexedDb to be able to recognize Firebase ids proceeding queries.
 * @type {'idxDb'}
 */
const INTERNAL_ID_PREFIX = 'idxDb';

const IDB_BINDING_KEY = `$${INTERNAL_ID_PREFIX}Bindings`;

/** @type {Record<Omit<WhereFilterOp, 'array-contains' | 'array-contains-any'>, PropertyKey<WhereClause>>} */
const OPERATORS_METHOD = {
  '==': 'equals',
  '!=': 'notEquals',
  '>': 'above',
  '>=': 'aboveOrEqual',
  '<': 'below',
  '<=': 'belowOrEqual',
  in: 'anyOf',
  'not-in': 'noneOf',
};

const DB_OPERATIONS = {
  CREATING: 'creating',
  UPDATING: 'updating',
  DELETING: 'deleting',
  EXECUTING: 'executing',
  READING: 'reading',
  CLEARING: 'clearing',
  CHANGE_CREATING: 'change:creating',
  CHANGE_UPDATING: 'change:updating',
  CHANGE_DELETING: 'change:deleting',
};

export default class GenericIndexedDb {
  /** @type {string} */
  collectionName;

  /** @type {string} */
  #fbCollectionPath;

  /** @type {string} */
  #mainCollectionDocumentId = '';

  /** @type {CryptoKey | null} */
  #publicEncryptionKey = null;

  /** @type {CryptoKey | null} */
  #decryptionKey = null;

  constructor(collectionPath) {
    this.setCollectionPath(collectionPath);
  }

  /**
   * Parse Firestore collection path saving IndexedDb collection name
   * and parent collections' documents ids if it is a subcollection path.
   * (Max 3 level collection nesting).
   */
  #parseCollectionName() {
    const [
      // eslint-disable-next-line no-unused-vars
      collectionName,
      collectionDocId,
      subCollectionName,
      subCollectionDocId,
      subSubCollectionName,
    ] = split(this.#fbCollectionPath, '/');

    if (!collectionDocId) {
      this.#mainCollectionDocumentId = '';
      this.collectionName = this.#fbCollectionPath;
      return;
    }

    if (subCollectionDocId && subSubCollectionName) {
      this.#mainCollectionDocumentId = `${collectionDocId}_${subCollectionDocId}`;
      this.collectionName = `sub_sub_${subSubCollectionName}`;
      return;
    }

    this.#mainCollectionDocumentId = collectionDocId;
    this.collectionName = `sub_${subCollectionName}`;
  }

  setCollectionPath(collectionPath) {
    this.#fbCollectionPath = collectionPath;
    this.#parseCollectionName();
  }

  /**
   * @todo rewrite logic into a static method that can be called from GenericDB to not create
   *   unnecessary instance of that class.
   */
  #validateCollection() {
    if (!IdxDb.isOpen()) {
      return false;
    }

    try {
      const collectionRef = this.getCollectionRef();
      return !!(collectionRef);
    } catch (err) {
      if (process.env.NODE_ENV === 'staging') {
        console.warn('Can\'t access IndexedDb collection', err);
      }
      return false;
    }
  }

  /**
   * Get reference to IndexedDb collection (table)
   *
   * @return {import('dexie').Table}
   */
  getCollectionRef() {
    const indexedDbCollection = IdxDb[this.collectionName];
    if (indexedDbCollection) {
      return indexedDbCollection;
    }

    throw new Error(`The "${this.collectionName}" collection not found`);
  }

  serializeInternalData(data) {
    return serializeIdxDbData(data, this.#fbCollectionPath);
  }

  /**
   * Implements Generic-Db method `getDocumentSnapshot` returning the same interface allowing user to check
   * if document `exists()`.
   *
   * @param {string} docId
   */
  async getDocSnapshot(docId) {
    const document = await this.read(docId);
    delete document.id;

    return {
      id: docId,
      data: () => document,
      exists: () => !!(document),
    };
  }

  /**
   * @param {string} docId
   * @return {IdxDbRef}
   */
  getDocRef(docId) {
    try {
      const collectionRef = this.getCollectionRef();
      const internalDocumentId = this.#convertToIndexedDbDocumentId(docId);
      const query = () => collectionRef.get(internalDocumentId);

      return {
        query,
        path: this.#fbCollectionPath,
        type: 'document',
        id: docId,
      };
    } catch (err) {
      this.#handleDbError(err, DB_OPERATIONS.READING, {
        documentId: docId,
      });
    }
  }

  /**
   * Build an IDB query with provided settings.
   *
   * @param {Array<Constraint> | Array<Array<any>> | null} [constraints]
   * @param {string | null} [queryOrderBy] - (Warning!) Should not be ordered by the string, boolean, object,
   * undefined or null field.
   * @param {'asc' | 'desc' | null} [orderDirection]
   * @param {number | null} [queryLimit]
   * @param {number | null} [offset]
   *
   * @return {IdxDbRef}
   */
  getQueryRef(
    constraints,
    queryOrderBy,
    orderDirection,
    queryLimit,
    offset,
  ) {
    try {
      const query = () => this.#buildQuery(constraints, queryOrderBy, orderDirection, queryLimit, offset);

      return {
        query,
        path: this.#fbCollectionPath,
        type: 'query',
        orderBy: queryOrderBy,
        orderDirection,
      };
    } catch (err) {
      this.#handleDbError(err, DB_OPERATIONS.EXECUTING, {
        constraints,
        orderBy: queryOrderBy,
        orderDirection,
        limit: queryLimit,
        offset,
      });
    }
  }

  /**
   * Optimizes recording change by handling previous changes of a document.
   * Might affect existing change record or even delete it.
   *
   * @param {DbChangeDocument} changeRecord
   * @return {Promise<DbChangeDocument | null>}
   */
  // eslint-disable-next-line class-methods-use-this
  async #optimizeChangeRecord(changeRecord) {
    const { operation, documentId, payload } = changeRecord;

    const changesCollectionRef = IdxDb.dbChanges;
    const existingDbChanges = await changesCollectionRef.where('documentId').equals(documentId).toArray();

    if (existingDbChanges.length > 0) {
      const lastDbChange = existingDbChanges[0];

      if (operation === 'update' && lastDbChange.operation === 'create') {
        // we don't need to record an update because we'll just handle the `create` during the sync
        return null;
      }

      if (operation === 'update' && lastDbChange.operation === 'update') {
        // merge existing `update` record with a new one without creating anything
        const updatedPayload = {
          ...changeRecord,
          id: lastDbChange.id,
          payload: { ...lastDbChange.payload, ...payload },
          timestamp: new Date(),
        };

        await changesCollectionRef.update(lastDbChange.id, updatedPayload);
        return null;
      }

      if (operation === 'delete') {
        // delete previous create/update record because the document was deleted anyway
        await changesCollectionRef.delete(lastDbChange.id);

        // data that was created offline, now is deleted, so we don't need to sync anything
        if (lastDbChange.operation === 'create') {
          return null;
        }
      }
    }

    return changeRecord;
  }

  /**
   * Creates document for database change
   *
   * @param {string} operation
   * @param {string} documentId
   * @param {Object} [payload]
   * @return {DbChangeDocument | null}
   */
  async #buildChangeRecord(operation, documentId, payload = null) {
    let changeRecord = {
      id: uid(),
      operation,
      collectionName: this.#fbCollectionPath,
      documentId,
      timestamp: new Date(),
    };

    if (payload) {
      changeRecord.payload = payload;
    }

    if (operation !== 'create') {
      changeRecord = await this.#optimizeChangeRecord(changeRecord);
    }

    return changeRecord;
  }

  /**
   * Saves database change
   *
   * @param {string} operation
   * @param {string} documentId
   * @param {Object} [payload]
   * @return {Promise<void>}
   */
  async #recordDbChange(operation, documentId, payload = null) {
    const originalCollectionPath = this.#fbCollectionPath;

    const dbChange = await this.#buildChangeRecord(operation, documentId, payload);

    if (dbChange === null) {
      return;
    }

    const changesCollectionRef = IdxDb.dbChanges;
    await changesCollectionRef.add(dbChange);

    // return instance to its original state
    this.setCollectionPath(originalCollectionPath);
  }

  /**
   * @param {Object} data
   * @param {string | null} [id]
   * @param {boolean} [merge] Merge data into existing data
   * @param {Timestamp} [overwriteCreateTimestamp] Overwrite create timestamp
   * @param {Timestamp} [overwriteUpdateTimestamp] Overwrite update timestamp
   * @param {boolean} [ignoreChange] Do not create a record in the dbChanges collection
   * @return {Promise<Object>} Created data
   */
  async create(
    data,
    id = null,
    merge = false,
    overwriteCreateTimestamp = null,
    overwriteUpdateTimestamp = null,
    ignoreChange = false,
  ) {
    if (!this.#validateCollection()) {
      return;
    }

    const timestamp = Timestamp.now();
    const documentId = id ? this.#convertToIndexedDbDocumentId(id) : this.#createNewDocumentId();
    let dataToCreate = await this.#encrypt(data);
    dataToCreate = await this.#processFirebaseFieldValues(dataToCreate);

    dataToCreate.createTimestamp = cloneDeep(overwriteCreateTimestamp || timestamp);
    dataToCreate.updateTimestamp = cloneDeep(overwriteUpdateTimestamp || timestamp);
    dataToCreate.updateBy = get(auth, ['currentUser', 'uid']) || null;
    dataToCreate.id = documentId;
    let currentOperation = id ? DB_OPERATIONS.UPDATING : DB_OPERATIONS.CREATING;

    try {
      const collectionRef = this.getCollectionRef();
      let existingDocument = null;

      if (id) {
        existingDocument = await this.read(id);
      }

      if (existingDocument) {
        await collectionRef.update(documentId, dataToCreate);
      } else {
        await collectionRef.add(dataToCreate);
      }

      if (network.offlineMode && !ignoreChange) {
        currentOperation = id ? DB_OPERATIONS.CHANGE_UPDATING : DB_OPERATIONS.CHANGE_CREATING;
        const dbChangeOperation = existingDocument ? 'update' : 'create';
        await this.#recordDbChange(
          dbChangeOperation,
          documentId,
          dbChangeOperation === 'update' ? dataToCreate : null,
        );
      }

      return this.serializeInternalData(dataToCreate);
    } catch (err) {
      this.#handleDbError(err, currentOperation, {
        dataToCreate,
        documentId: dataToCreate.id,
        merge,
      });
    }
  }

  #createNewDocumentId() {
    const newFirestoreDocumentId = this.#createNewFirestoreDocumentId();

    return this.#mainCollectionDocumentId
      ? `${INTERNAL_ID_PREFIX}_${this.#mainCollectionDocumentId}_${newFirestoreDocumentId}`
      : `${INTERNAL_ID_PREFIX}_${newFirestoreDocumentId}`;
  }

  #createNewFirestoreDocumentId() {
    const fbNewDocRef = doc(this.#getFirebaseCollectionRef());
    return fbNewDocRef.id;
  }

  /**
   * Get reference to Firebase collection
   *
   * @return {import('firebase/firestore').CollectionReference}
   */
  #getFirebaseCollectionRef() {
    const pathSections = split(this.#fbCollectionPath, '/');
    return collection(db, ...pathSections);
  }

  /**
   * Set specific data on a document in the collection
   * @param {Object} data Data to set
   * @param {string} id Document id to set data on
   * @param {boolean} ignoreChange do not create a record in the dbChanges collection
   * */
  async set(data, id = null, ignoreChange = false) {
    if (!this.#validateCollection()) {
      return;
    }

    if (!id) {
      throw new Error(`Document id is missing (${this.#fbCollectionPath} collection)`);
    }

    const internalDocumentId = this.#convertToIndexedDbDocumentId(id);
    let dataToSave = cloneDeep(data);
    let currentOperation = DB_OPERATIONS.UPDATING;

    try {
      dataToSave = await this.#encrypt(dataToSave);

      await IdxDb.transaction(
        'rw',
        this.getCollectionRef(),
        IdxDb.dbChanges,
        async () => {
          dataToSave = await this.#processFirebaseFieldValues(dataToSave, id);
          dataToSave.updateTimestamp = Timestamp.now();
          dataToSave.updateBy = get(auth, ['currentUser', 'uid']) || null;

          if (dataToSave.id) {
            delete dataToSave.id;
          }

          const collectionsRef = this.getCollectionRef();
          const updateResult = await collectionsRef.update(internalDocumentId, dataToSave);
          if (updateResult === 0) {
            dataToSave.id = internalDocumentId;
            await collectionsRef.add(dataToSave);

            if (!ignoreChange && network.offlineMode) {
              currentOperation = DB_OPERATIONS.CHANGE_CREATING;
              await this.#recordDbChange('create', internalDocumentId);
            }

            return;
          }

          if (!ignoreChange && network.offlineMode) {
          // using `transaction` to make sure we always use the latest data optimizing update record
            await IdxDb.transaction('rw', IdxDb.dbChanges, async () => {
              currentOperation = DB_OPERATIONS.CHANGE_UPDATING;
              return this.#recordDbChange('update', internalDocumentId, dataToSave);
            });
          }
        },
      );
    } catch (err) {
      this.#handleDbError(err, currentOperation, {
        data,
        documentId: id,
      });
    }
  }

  /**
   * @param {Object} data
   * @param {string | null} parentSchemaKey
   * @return {Promise<Object>}
   */
  async #encryptData(data, parentSchemaKey = null) {
    const actions = Object.keys(data).map(async (key) => {
      const schemaKey = parentSchemaKey ? `${parentSchemaKey}.${key}` : key;

      const isFieldValue = data[key] instanceof FieldValue;
      const value = isFieldValue ? getFieldValueData(data[key]) : data[key];

      if (ENCRYPTED_FIELDS[this.#fbCollectionPath]?.includes(schemaKey)) {
        if (Array.isArray(value)) {
          const encryptedArray = await Promise.all(value.map(async (item) => {
            if (typeof item === 'object' && !isEmpty(value)) {
              return this.#encryptData(item, schemaKey);
            }
            return createEncryptedDataObject(item, this.#publicEncryptionKey);
          }));

          // If original value is a FieldValue, we need to return a FieldValue with encrypted data
          // eslint-disable-next-line no-param-reassign
          data[key] = isFieldValue
            ? getFieldValueWithUpdatedData(data[key], encryptedArray)
            : encryptedArray;
          return;
        }

        if (typeof value === 'object' && !isEmpty(value)) {
          // eslint-disable-next-line no-param-reassign
          data[key] = await this.#encryptData(value, schemaKey);
          return;
        }

        try {
          // eslint-disable-next-line no-param-reassign
          data[key] = await createEncryptedDataObject(value, this.#publicEncryptionKey);
        } catch (err) {
          console.error('Encryption error', value, err);
        }
      }
    });

    await AppUtils.allSettled(actions);
    return data;
  }

  /**
   * @param {Object} data
   * @return {Promise<Object>}
   * */
  async #encrypt(data) {
    if (!data) {
      return data;
    }

    await this.#initPublicEncryptionKey();

    if (Array.isArray(data)) {
      return Promise.all(data.map((document) => this.#encryptData(cloneDeep(document))));
    }

    return this.#encryptData(cloneDeep(data));
  }

  /**
   * Set public encryption key in the class property to be used later
   */
  async #initPublicEncryptionKey() {
    if (!this.#publicEncryptionKey) {
      const publicEncryptionKey = [BACKUP_DATA, BACKUP_NOTES, AUTH_SESSIONS_INDEX_DB].includes(this.collectionName)
        ? 'backupPublicKey' : 'publicKey';

      if (publicEncryptionKey === 'backupPublicKey' && !await readSecret('backupPublicKey')) {
        const encryptionPassword = auth.currentUser.metadata.creationTime;

        if (encryptionPassword) {
          await addBackupDecryptionKey(encryptionPassword, true);
          await clearBackups();
        } else {
          logEvent('There is no creationTime in the user metadata', {
            severity: 'info',
            context: {
              context_data: { userId: auth.currentUser.uid },
            },
          });
        }
      }

      const publicKey = await readSecret(publicEncryptionKey);
      this.#publicEncryptionKey = publicKey.value;
    }
  }

  /**
   * @param {Object} data
   * @return {Promise<Object>}
   * */
  async #decrypt(data) {
    return decrypt(cloneDeep(data), this.#fbCollectionPath);
  }

  /**
   * @param {Object} data
   * @param {string} [id]
   * @return {Promise<Object>}
   */
  async #processFirebaseFieldValues(data, id) {
    /* eslint-disable no-underscore-dangle */
    const dataCopy = { ...data };

    const actions = Object.keys(dataCopy).map(async (key) => {
      const value = data[key];

      if (!(value instanceof FieldValue)) {
        return;
      }

      if (value._methodName === 'arrayUnion' && id) {
        const arrays = await this.#getTargetAndInitArrays(key, value, id);
        if (!arrays) {
          return;
        }

        const { targetArray, initArray } = arrays;
        // not only concat but also look for duplicates
        dataCopy[key] = unionWith(initArray, targetArray, isEqual);
        return;
      }

      if (value._methodName === 'arrayRemove' && id) {
        const arrays = await this.#getTargetAndInitArrays(key, value, id);
        if (!arrays) {
          return;
        }

        const { targetArray, initArray } = arrays;

        // the equivalent to lodash's without() function but with support of removing objects
        dataCopy[key] = reject(initArray, (initItem) => {
          return some(targetArray, (withoutItem) => {
            // isEqual() doesn't work right with ArrayBuffer values here, so in order to compare
            // encrypted data we will just compare their hashStrings
            return isEqualWith(initItem, withoutItem, (initValue, withoutValue) => {
              if (initValue && typeof initValue === 'object' && initValue.encrypted) {
                return initValue.hashString === withoutValue.hashString;
              }
              return undefined;
            });
          });
        });
      }

      if (value._methodName === 'serverTimestamp') {
        dataCopy[key] = Timestamp.now();
      }
    });

    await AppUtils.allSettled(actions);
    return dataCopy;
    /* eslint-enable no-underscore-dangle */
  }

  /**
   * @param {string} field - field name of the array in document
   * @param {FieldValue} value
   * @param {string} docId - id of the document that is being mutating
   * @return {Promise<{targetArray: [], initArray: []}> | undefined}
   */
  async #getTargetAndInitArrays(field, value, docId) {
    const targetArray = getFieldValueData(value);
    if (!targetArray || !Array.isArray(targetArray)) {
      return;
    }

    const fullDocument = await this.read(docId, true);
    if (!fullDocument) {
      return { targetArray, initArray: [] };
    }

    let initArray = fullDocument[field];
    if (!Array.isArray(initArray)) {
      initArray = [];
    }

    return { targetArray, initArray };
  }

  /**
   * @param {string} id
   * @param {boolean} [ignoreEncryption]
   * @return {Promise<Object>}
   */
  async read(id, ignoreEncryption = false) {
    if (!this.#validateCollection()) {
      return;
    }

    try {
      const collectionRef = this.getCollectionRef();
      const internalDocumentId = this.#convertToIndexedDbDocumentId(id);

      let document = await collectionRef.get(internalDocumentId);
      if (!document) {
        return document;
      }

      if (!ignoreEncryption) {
        document = await this.#decrypt(cloneDeep(document));
      }

      document = this.serializeInternalData(document);

      return document;
    } catch (err) {
      this.#handleDbError(err, DB_OPERATIONS.READING, { documentId: id });
    }
  }

  /**
   * @param {string} id
   * @return {string}
   */
  #convertToIndexedDbDocumentId(id) {
    let prefix = '';

    if (!id.includes(INTERNAL_ID_PREFIX)) {
      prefix += `${INTERNAL_ID_PREFIX}_`;

      if (this.#mainCollectionDocumentId && !id.includes(this.#mainCollectionDocumentId)) {
        prefix += `${this.#mainCollectionDocumentId}_`;
      }
    }

    return prefix + id;
  }

  /**
   * Read all documents in the collection following constraints
   *
   * @param {Array<Constraint> | Array<Array<any>> | null} [constraints]
   * @param {string | null} [queryOrderBy] - (Warning!) Should not be ordered by the string, boolean, object,
   * undefined or null field.
   * @param {'asc' | 'desc' | null} [orderDirection]
   * @param {number | null} [queryLimit]
   * @param {number | null} [offset]
   * @return {Promise<Array<{id: string} & Object>>}
   */
  async readAll(
    constraints = null,
    queryOrderBy = null,
    orderDirection = null,
    queryLimit = null,
    offset = null,
  ) {
    if (!this.#validateCollection()) {
      return [];
    }

    try {
      let documents = await this.#buildQuery(
        constraints,
        queryOrderBy,
        orderDirection,
        queryLimit,
        offset,
      );

      const decryptedResult = await this.#decrypt(documents);
      documents = this.serializeInternalData(decryptedResult);

      if (queryOrderBy) {
        documents = orderBy(documents, queryOrderBy, orderDirection);
      }

      return documents;
    } catch (err) {
      this.#handleDbError(err, DB_OPERATIONS.EXECUTING, {
        constraints,
        orderBy: queryOrderBy,
        orderDirection,
        limit: queryLimit,
        offset,
      });
    }
  }

  /**
   * Create query from settings and return unresolved Promise
   *
   * @param {Array<Constraint> | Array<Array<any>> | null} [constraints]
   * @param {string | null} [queryOrderBy]
   * @param {'asc' | 'desc' | null} [orderDirection]
   * @param {number | null} [queryLimit]
   * @param {number | null} [offset]
   * @return {Promise<any[]>}
   */
  #buildQuery(
    constraints,
    queryOrderBy,
    orderDirection,
    queryLimit,
    offset,
  ) {
    let queryBuilder = this.getCollectionRef();
    let requestedQuery;

    if (isArray(constraints) && !isEmpty(constraints)) {
      queryBuilder = this.#processConstraints(queryBuilder, constraints);
    } else if (queryOrderBy) {
      queryBuilder = queryBuilder.orderBy(queryOrderBy);
    }

    if (this.#mainCollectionDocumentId) {
      queryBuilder = queryBuilder.filter((data) => data.id.includes(this.#mainCollectionDocumentId));
    }

    if (orderDirection === 'desc') {
      // `asc` used by default direction, so the result needs to be reversed to make `desc` order direction
      queryBuilder = queryBuilder.reverse();
    }

    if (queryLimit) {
      queryBuilder = queryBuilder.limit(queryLimit);
    }

    if (offset) {
      queryBuilder = queryBuilder.offset(offset);
    }

    if (queryOrderBy && isArray(constraints) && !isEmpty(constraints)) {
      /**
       * We will use `sortBy` if there are constraints to filter by, so that the ordering would be done
       * on the resulting array rather than letting the backend implementation do the sorting.
       */
      requestedQuery = queryBuilder.sortBy(queryOrderBy);
    } else {
      requestedQuery = queryBuilder.toArray();
    }

    return requestedQuery;
  }

  /**
   * @param {Table} table
   * @param {Array<Constraint>} constraints
   * @return {Collection}
   */
  #processConstraints(table, constraints) {
    let queryBuilder = table;

    if (constraints.length === 1) {
      const constraint = constraints[0];
      return this.#processQueryFromSingleConstraint(queryBuilder, constraint);
    }

    const constraintsGroupedByOperator = groupBy(constraints, (constraint) => (constraint[1]));
    if (Object.keys(constraintsGroupedByOperator).length === 1) {
      // There are constraints with only one operator.
      return this.#processQueryFromConstraints(queryBuilder, constraints);
    }

    // If we have many operators we take the first one as the main, proceeding `.where()` query with it
    //  and process other constraints using `.filter()` method.
    const mainConstraintsChunk = Object.values(constraintsGroupedByOperator)[0];
    queryBuilder = this.#processQueryFromConstraints(queryBuilder, mainConstraintsChunk);

    const otherGroupedConstraints = omit(constraintsGroupedByOperator, [mainConstraintsChunk[0][1]]);
    forEach(otherGroupedConstraints, (constraintsChunk) => {
      queryBuilder = this.#processFilterConstraints(queryBuilder, constraintsChunk);
    });

    return queryBuilder;
  }

  /**
   * @param {Table} table
   * @param {Constraint} constraint
   * @return {Collection}
   */
  #processQueryFromSingleConstraint(table, constraint) {
    const firebaseOperator = constraint[1];
    const indexField = this.#getConstraintIndexField(constraint);
    const fieldValues = this.#getConstraintValue(constraint);

    if (typeof fieldValues === 'boolean' || [null, undefined].includes(fieldValues)) {
      // Indexing by boolean, null or undefined fields is unable with IndexedDb - process it using `filter()` method
      return table.filter((data) => this.#getFilterConditionByConstraint(data, constraint));
    }

    return table.where(indexField)[OPERATORS_METHOD[firebaseOperator]](fieldValues);
  }

  /**
   * @param {Constraint} constraint
   */
  // eslint-disable-next-line class-methods-use-this
  #getConstraintIndexField(constraint) {
    return constraint[0] instanceof FieldPath ? 'id' : constraint[0];
  }

  /**
   * @param {Constraint} constraint
   */
  #getConstraintValue(constraint) {
    const value = constraint[2];

    if (value instanceof Timestamp) {
      return value.toMillis();
    }

    if (!(constraint[0] instanceof FieldPath) && constraint[0] !== 'id') {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((id) => this.#convertToIndexedDbDocumentId(id));
    }

    return this.#convertToIndexedDbDocumentId(value);
  }

  /**
   * @param {Table} table
   * @param {Array<Constraint>} constraints
   * @return {Collection}
   */
  #processQueryFromConstraints(table, constraints) {
    let whereConstraints = constraints;
    const filterConstraints = constraints.filter((constraint) => {
      // Indexing by boolean, null or undefined fields is unable with IndexedDb - process it using `filter()` method
      return (typeof constraint[2] === 'boolean' || [null, undefined].includes(constraint[2]));
    });

    if (filterConstraints.length) {
      whereConstraints = constraints.filter((constraint) => {
        return typeof constraint[2] !== 'boolean' && !([null, undefined].includes(constraint[2]));
      });

      if (!whereConstraints.length) {
        return this.#processFilterConstraints(table, filterConstraints);
      }
    }

    const constraintsIndexes = whereConstraints.map((constraint) => (
      this.#getConstraintIndexField(constraint)));

    const operator = whereConstraints[0][1];
    const whereIndex = whereConstraints.length === 1
      ? this.#getConstraintIndexField(whereConstraints[0]) // index field of the only constraint
      : `[${constraintsIndexes.join('+')}]`; // [field1+field2]
    const whereValues = whereConstraints.length === 1
      ? this.#getConstraintValue(whereConstraints[0]) // search field of the only constraint
      : whereConstraints.map((constraint) => this.#getConstraintValue(constraint));

    let queryBuilder = table.where(whereIndex)[OPERATORS_METHOD[operator]](whereValues);

    if (filterConstraints.length) {
      queryBuilder = this.#processFilterConstraints(queryBuilder, filterConstraints);
    }

    return queryBuilder;
  }

  /**
   * @param {Table | Collection} queryBuilder
   * @param {Array<Constraint>} constraints
   * @return {Collection}
   */
  #processFilterConstraints(queryBuilder, constraints) {
    return queryBuilder.filter((data) => {
      return every(
        constraints.map((constraint) => this.#getFilterConditionByConstraint(data, constraint)),
      );
    });
  }

  /**
   * @param {Object} data
   * @param {Constraint} constraint
   * @return {boolean}
   */
  // eslint-disable-next-line class-methods-use-this
  #getFilterConditionByConstraint(data, constraint) {
    /** @type {WhereFilterOp} */
    const operator = constraint[1];
    const field = this.#getConstraintIndexField(constraint);
    const value = this.#getConstraintValue(constraint);
    const dataProperty = this.#getPropertyFromDocument(data, field);
    let condition;

    switch (operator) {
      case '==': condition = dataProperty === value;
        break;
      case '!=': condition = dataProperty !== value;
        break;
      case '<': condition = dataProperty < value;
        break;
      case '<=': condition = dataProperty <= value;
        break;
      case '>': condition = dataProperty > value;
        break;
      case '>=': condition = dataProperty >= value;
        break;
      case 'in': condition = value.includes(dataProperty);
        break;
      case 'not-in': condition = !(value.includes(dataProperty));
        break;
      default: condition = true;
    }

    return condition;
  }

  /**
   * @param {Object} data
   * @param {string} field
   */
  // eslint-disable-next-line class-methods-use-this
  #getPropertyFromDocument(data, field) {
    let result = data[field];

    if (isTimestampObject(result)) {
      result = firebaseTimestampObjectToTimestamp(result).toMillis();
    }

    return result;
  }

  /**
   * @param {Object & { id: string }} data
   * @return {Promise<Object>} updated object
   */
  async update(data) {
    if (!this.#validateCollection()) {
      return;
    }

    const { id } = data;

    let currentOperation = DB_OPERATIONS.UPDATING;
    let updatedData = cloneDeep(data);

    try {
      if (!id) {
        throw new Error('Document id is not provided');
      }

      const internalDocumentId = this.#convertToIndexedDbDocumentId(id);
      updatedData = await this.#encrypt(updatedData);

      await IdxDb.transaction(
        'rw',
        this.getCollectionRef(),
        IdxDb.dbChanges,
        async () => {
          updatedData = await this.#processFirebaseFieldValues(updatedData, id);
          updatedData = {
            ...omit(updatedData, 'id'),
            updateTimestamp: Timestamp.now(),
            updateBy: get(auth, ['currentUser', 'uid']) || null,
          };

          await this.getCollectionRef().update(internalDocumentId, updatedData);

          if (network.offlineMode) {
          // using `transaction` to make sure we always use the latest data optimizing update record
            await IdxDb.transaction('rw', IdxDb.dbChanges, async () => {
              currentOperation = DB_OPERATIONS.CHANGE_UPDATING;
              return this.#recordDbChange('update', internalDocumentId, updatedData);
            });
          }
        },
      );
      return this.serializeInternalData(updatedData);
    } catch (err) {
      this.#handleDbError(err, currentOperation, { data, documentId: id });
    }
  }

  /**
   * @param {string} id
   * @param {boolean} ignoreChange do not create a record in the dbChanges collection
   * @return {Promise<void>}
   */
  async delete(id, ignoreChange) {
    if (!this.#validateCollection()) {
      return;
    }

    let currentOperation = DB_OPERATIONS.DELETING;

    try {
      const collectionRef = this.getCollectionRef();
      const internalDocumentId = this.#convertToIndexedDbDocumentId(id);

      if (network.offlineMode && !ignoreChange) {
        currentOperation = DB_OPERATIONS.CHANGE_DELETING;
        await this.#recordDbChange('delete', internalDocumentId);
      }

      return await collectionRef.delete(internalDocumentId);
    } catch (err) {
      this.#handleDbError(err, currentOperation, { documentId: id });
    }
  }

  /**
   * Clear current table
   * @return {Promise<void>}
   */
  async clearTable() {
    try {
      const collectionRef = this.getCollectionRef();
      await collectionRef.clear();
    } catch (err) {
      this.#handleDbError(err, DB_OPERATIONS.CLEARING, { table: this.collectionName });
    }
  }

  /**
   * @param {any} context
   * @param {string} param
   * @param {Array<Constraint> | Array<Array<any>> | null} [constraints]
   * @param {string | null} [queryOrderBy] - (Warning!) Should not be ordered by the string, boolean, object,
   * undefined or null field.
   * @param {'asc' | 'desc' | null} [orderDirection]
   * @param {number | null} [queryLimit]
   */
  async bindAll(
    context,
    param,
    constraints = null,
    queryOrderBy = null,
    orderDirection = null,
    queryLimit = null,
  ) {
    if (!this.#validateCollection()) {
      return;
    }

    try {
      const ref = this.getQueryRef(constraints, queryOrderBy, orderDirection, queryLimit);
      const observable = liveQuery(ref.query);

      return await this.#subscribeToQuery(observable, context, param, ref);
    } catch (err) {
      this.#handleDbError(err, DB_OPERATIONS.EXECUTING, {
        constraints,
        orderBy: queryOrderBy,
        orderDirection,
        limit: queryLimit,
      });
    }
  }

  /**
   * @param {any} context
   * @param {string} param
   * @param {string} docId
   */
  async bindDocument(context, param, docId) {
    if (!this.#validateCollection()) {
      return;
    }

    try {
      const ref = this.getDocRef(docId);
      const observable = liveQuery(ref.query);
      return await this.#subscribeToQuery(observable, context, param, ref);
    } catch (err) {
      this.#handleDbError(err, DB_OPERATIONS.EXECUTING, {
        documentId: docId,
      });
    }
  }

  /**
   * @param {import('dexie').Observable} observable
   * @param {Object} context
   * @param {string} param
   * @param {IdxDbRef} ref
   * @return {Promise<Object & { id: string }>}
   */
  // eslint-disable-next-line class-methods-use-this
  async #subscribeToQuery(observable, context, param, ref) {
    return new Promise((res, rej) => {
      const unsubscribe = observable.subscribe({
        next: async (docs) => {
          let resultData = await this.#decrypt(docs);
          resultData = this.serializeInternalData(resultData);

          if (ref?.orderBy) {
            resultData = orderBy(resultData, ref.orderBy, ref.orderDirection);
          }

          context[param] = resultData;
          res(resultData);
        },
        error: (error) => {
          rej(error);
        },
      });

      if (!context[IDB_BINDING_KEY] || !(context[IDB_BINDING_KEY] instanceof Map)) {
        context[IDB_BINDING_KEY] = new Map();
      }

      context[IDB_BINDING_KEY].set(param, unsubscribe);
    });
  }

  /**
   * @param {Object} context
   * @param {string} param
   * @return {void}
   */
  // eslint-disable-next-line class-methods-use-this
  unbind(context, param) {
    if (!context || !context[IDB_BINDING_KEY] || !(context[IDB_BINDING_KEY] instanceof Map)) {
      return;
    }

    const unbinding = context[IDB_BINDING_KEY].get(param);
    if (!unbinding?.unsubscribe || typeof unbinding?.unsubscribe !== 'function') {
      return;
    }

    unbinding.unsubscribe();
    context[IDB_BINDING_KEY].delete(param);
  }

  /**
   * @param {Array<Array<string>>} constraints
   * @param {string} queryOrderBy
   * @param {'asc' | 'desc' } orderDirection
   * @param {number} queryLimit
   * @param {number | null} [offset]
   */
  async readPaginated(constraints, queryOrderBy, orderDirection, queryLimit, offset = null) {
    if (!this.#validateCollection()) {
      return {
        docs: [],
        size: 0,
        empty: true,
      };
    }

    try {
      let documents = await this.#buildQuery(constraints, queryOrderBy, orderDirection, queryLimit, offset);

      const decryptedResult = await this.#decrypt(documents);

      documents = this.serializeInternalData(decryptedResult);

      if (queryOrderBy) {
        documents = orderBy(documents, queryOrderBy, orderDirection);
      }

      return {
        docs: documents.map((document) => ({
          id: document.id,
          data: () => document,
        })),
        size: documents.length,
        empty: documents.length === 0,
      };
    } catch (err) {
      this.#handleDbError(err, DB_OPERATIONS.EXECUTING, {
        constraints,
        orderBy: queryOrderBy,
        orderDirection,
        limit: queryLimit,
        offset,
      });
    }
  }

  /**
   * @param {Error|string} err
   * @param {DbOperation} operationName
   * @param {Object} logData
   */
  #handleDbError(err, operationName, logData) {
    const message = typeof err === 'string' ? err : (err?.message ?? String(err));
    const operationSubject = ['executing', 'getting'].includes(operationName) ? 'query' : 'document';
    const errMessage = `Error ${operationName} a "${this.#fbCollectionPath}" ${operationSubject}: ${message}`;

    // most likely, this error in online mode means that we
    // are trying to update collection that is not to be used offline
    if (message.includes('collection not found') && !network.offlineMode) {
      console.warn(errMessage);
      return;
    }

    console.error(errMessage);

    if (isQuotaExceededError(err)) {
      // backups can be recovered by freeing up space and retrying the write, so warning the user
      // is up to their caller - every other collection has nothing else to fall back on
      if (![BACKUP_DATA, BACKUP_NOTES].includes(this.collectionName)) {
        notifyStorageQuotaExceeded();
      }
      logStorageQuotaEvent(errMessage, { ...logData, collectionPath: this.#fbCollectionPath })
        .catch((logErr) => console.error(logErr));
    } else {
      logEvent(errMessage, {
        context: {
          context_data: {
            ...logData,
            collectionPath: this.#fbCollectionPath,
          },
        },
      });
    }

    const error = new Error(errMessage);
    // keep the original error reachable: callers detect a quota error by its `name`, which a
    // plain re-thrown Error would lose (Dexie's ModifyError does not carry the name either)
    error.inner = err;
    throw error;
  }

  /**
   * Bulk update documents in the collection.
   * @param {{ id: string }[]} items
   * @param {Record<string, any>} updateData
   * @return {Promise<Object[] | void>} array of updated objects
   */
  async bulkUpdate(items, updateData) {
    if (!this.#validateCollection()) {
      return;
    }

    const promises = items
      .filter((item) => item?.id)
      .map((item) => {
        return this.update({ ...item, ...updateData });
      });

    return Promise.all(promises);
  }
}
